/* import __COLOCATED_TEMPLATE__ from './salesforce.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import Component from '@glimmer/component';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType, SyncBehaviorType } from 'embercom/models/import-service/import-source';
import { type SyncSteps } from '../../filterable-list';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
interface Args {
  modal: any;
  title?: string;
  onNavigationBack?: () => void;
  onNavigationNext?: (viewId?: string) => void;
  showBackButton?: boolean;
  onModalClose: () => void;
  setSyncStep: (step: SyncSteps) => void;
  isReconfiguring?: boolean;
  connectionType: string;
  sourceType: SourceType;
  existingImportSource?: ImportSource;
}

export default class SalesforceSyncModal extends Component<Args> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare openCenteredWindowService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare appService: any;

  @tracked importSource: ImportSource | undefined;
  @tracked isConnecting = false;
  @tracked syncBehavior: SyncBehaviorType = SyncBehaviorType.Sync;
  @tracked isLoadingOauth = true;
  @tracked loadingMessage = 'knowledge-hub.sync-modal.box.complete-oauth';
  @tracked credentials: any;
  @tracked selectedItems: any[] = [];
  @tracked items: any[] = [];
  @tracked allowedFileTypes: string[] = [];
  @tracked connectionType = 'salesforce-src';
  @tracked sourceType = SourceType.Salesforce;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // check if we have an existing import source (reconfigure)
    if (this.args.existingImportSource) {
      this.importSource = this.args.existingImportSource;
    }
    taskFor(this.startOauthFlow).perform();
  }

  @action
  async initiateConnection() {
    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    try {
      await this.startSync();
      this.args.onModalClose();
    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }

  @task({ drop: true })
  *startOauthFlow(): TaskGenerator<void> {
    // for reconfigure, we already have an import source
    if (!this.importSource) {
      this.importSource = yield this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Salesforce,
      });
    }

    if (!this.importSource) {
      return;
    }

    if (!this.args.existingImportSource) {
      let url = this.knowledgeHubService.getOAuthURL(this.importSource.id);

      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      let window = this.openCenteredWindowService.setup(url, 800, 1280, 'Import');
      if (window) {
        window.focus();
        yield this.waitForWindowClose(window);
        // Window was closed - now we're loading content
        this.loadingMessage = 'knowledge-hub.sync-modal.box.loading-content';
      }

      yield this.importSource.reload();
    }

    if (this.importSource.hasCredentials || this.args.existingImportSource) {
      // We're loading content
      this.loadingMessage = 'knowledge-hub.sync-modal.box.loading-content';
    }

    if (this.importSource.context?.selectedItems) {
      this.selectedItems = this.importSource.context.selectedItems;
    }

    this.allowedFileTypes = this.importSource?.fileTypes ?? [];

    this.isLoadingOauth = false;

    if (!this.importSource.hasCredentials && !this.args.existingImportSource) {
      this.args.onModalClose();
      yield this.importSource.remove({ notify: false });
    }
  }

  private waitForWindowClose(window: any): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }

  get syncDisabled() {
    return !this.importSource;
  }

  @action
  async startSync() {
    // todo
  }

  @action
  async cancelSync() {
    // todo
  }

  @action
  onSelectionChange(selectedSet: any[]) {
    this.selectedItems = selectedSet;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Salesforce': typeof SalesforceSyncModal;
    'knowledge-hub/setup-modal/sync-modal/salesforce': typeof SalesforceSyncModal;
  }
}
